import { apiService } from '@/service/api'

export interface PlcVariable {
  name: string
  type: string
  section: string
}

export interface PlcNode {
  local_id: string
  kind: string
  label: string
  negated: boolean
  /** Drawing coordinates from the PLCopen XML (layout ships with logic); null when absent. */
  x: number | null
  y: number | null
  width: number | null
  height: number | null
}

export interface PlcEdge {
  source: string
  target: string
}

export interface PlcStRule {
  target: string
  expression: string
  /** A condition of the form `LOOP(...)` marks iteration context, not a boolean guard. */
  conditions: string[]
}

export interface PlcPou {
  name: string
  language: string
  variables: PlcVariable[]
  nodes: PlcNode[]
  edges: PlcEdge[]
  coil_logic: Record<string, string>
  /** Rules derived from Structured Text bodies: "expression" or "expression WHEN condition". */
  st_logic: Record<string, string[]>
  /** Structured form of st_logic, in source order — what the logic diagram renders. */
  st_rules: PlcStRule[]
}

export interface PlcDocument {
  pous: PlcPou[]
}

export interface PlcParseResponse {
  filename: string
  document: PlcDocument
}

/**
 * Multipart POST of one PLCopen XML file to the synchronous parse endpoint.
 * No `Content-Type` header on purpose — the browser sets it with the boundary.
 */
export async function parsePlcFile(file: File): Promise<PlcParseResponse> {
  const form = new FormData()
  form.append('file', file)
  const response = await apiService.fetchOrError(apiService.getUrl('v1/plc/parse'), {
    method: 'POST',
    body: form,
  })
  return response.json()
}

export interface PlcDocumentResponse {
  document_id: string
  document: PlcDocument
}

/** Load the stored parse artifact of a catalog PLC document (worker output). */
export async function fetchPlcDocument(documentId: string): Promise<PlcDocumentResponse> {
  const response = await apiService.fetchOrError(
    apiService.getUrl(`v1/plc/documents/${documentId}`),
    { method: 'GET' }
  )
  return response.json()
}
